import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { pushSupported, getPushState, enablePush, disablePush, sendTestPush } from "@/lib/push";

const DISMISS_KEY = "pushPromptDismissed";
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent || "");
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

async function tryEnable(t) {
  if (isIOS() && !isStandalone()) {
    toast.message(t("push_ios_hint"));
    return { ok: false, reason: "ios_install" };
  }
  const res = await enablePush();
  if (res.ok) toast.success(t("push_enabled_toast"));
  else if (res.reason === "denied") toast.error(t("push_denied"));
  else if (res.reason === "unsupported") toast.error(t("push_unsupported"));
  return res;
}

// Bannière discrète (tableau de bord) proposant d'activer les notifications.
export function PushBanner({ t }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!pushSupported() || localStorage.getItem(DISMISS_KEY) === "1") return;
      const st = await getPushState();
      if (alive && !st.subscribed && st.permission === "default") setShow(true);
    })();
    return () => { alive = false; };
  }, []);

  if (!show) return null;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); };
  const activate = async () => {
    const res = await tryEnable(t);
    if (res.ok || res.reason === "ios_install") dismiss();
  };

  return (
    <div data-testid="push-prompt" className="rounded-2xl bg-majorelle/10 border border-majorelle/30 p-4 flex items-start gap-3">
      <span className="h-10 w-10 rounded-xl bg-majorelle text-white flex items-center justify-center shrink-0">
        <Icons.BellRing className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-bold">{t("push_enable")}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{t("push_enable_desc")}</p>
        <div className="flex gap-2 mt-3">
          <button onClick={activate} data-testid="push-prompt-enable"
            className="h-10 px-4 rounded-xl bg-majorelle text-white font-bold text-sm active:scale-95 transition-transform">
            {t("push_activate")}
          </button>
          <button onClick={dismiss} data-testid="push-prompt-dismiss"
            className="h-10 px-4 rounded-xl bg-secondary text-secondary-foreground font-semibold text-sm">
            {t("push_later")}
          </button>
        </div>
      </div>
      <button onClick={dismiss} data-testid="push-prompt-close" className="h-7 w-7 rounded-full hover:bg-black/5 flex items-center justify-center shrink-0">
        <Icons.X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Réglage des notifications push (écran « Plus »).
export function PushSettings({ t }) {
  const [state, setState] = useState({ supported: true, subscribed: false, permission: "default" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => setState(await getPushState());
  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (state.subscribed) {
        await disablePush();
        toast.success(t("push_disabled_toast"));
      } else {
        await tryEnable(t);
      }
      await refresh();
    } finally { setBusy(false); }
  };

  const test = async () => {
    const ok = await sendTestPush();
    if (ok) toast.success(t("push_test_sent"));
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-4 space-y-3" data-testid="push-settings">
      <p className="font-bold">{t("push_settings")}</p>
      {!state.supported ? (
        <p className="text-sm text-muted-foreground">{t("push_unsupported")}</p>
      ) : state.permission === "denied" ? (
        <p className="text-sm text-muted-foreground">{t("push_denied")}</p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="font-semibold flex items-center gap-2">
              <Icons.BellRing className="h-5 w-5" /> {state.subscribed ? t("push_on") : t("push_off")}
            </span>
            <button onClick={toggle} disabled={busy} data-testid="push-toggle"
              className={`h-8 w-14 rounded-full p-1 transition-colors disabled:opacity-50 ${state.subscribed ? "bg-brand" : "bg-secondary"}`}>
              <span className={`block h-6 w-6 rounded-full bg-white transition-transform ${state.subscribed ? "translate-x-6 rtl:-translate-x-6" : ""}`} />
            </button>
          </div>
          {state.subscribed && (
            <button onClick={test} data-testid="push-test-btn" className="text-sm font-semibold text-majorelle">
              {t("push_test")}
            </button>
          )}
          <p className="text-xs text-muted-foreground">{t("push_ios_hint")}</p>
        </>
      )}
    </div>
  );
}
